import { MenuItem, PermissionType } from "lib-klab-core";

export const STAFF_AREA_CODE = "STAFF";
export const ACCOUNTING_AREA_CODE = "ACCOUNTING";
export const LOGISTICS_AREA_CODE = "LOGISTICS";
export const TECHNOLOGY_AREA_CODE = "TECHNOLOGY";

const KLAB_TIME_CORE_CODES = [
  "DASHBOARD",
  "EMPLOYEES",
  "TIMESHEET",
  "REQUESTS",
  "SOLICITUDES",
  "REPORTS",
] as const;

const KLAB_TIME_CORE_ROUTE_PREFIXES = [
  "/admin/dashboard",
  "/admin/employees",
  "/admin/timesheet",
  "/admin/requests",
  "/admin/reports",
] as const;

const KLAB_TIME_AUXILIARY_CODES = ["SETTINGS"] as const;
const KLAB_TIME_AUXILIARY_ROUTE_PREFIXES = ["/admin/settings"] as const;

const GLOBAL_CODES = ["NOTIFICATIONS-HISTORY", "KLABCHAT"] as const;
const GLOBAL_ROUTE_PREFIXES = [
  "/admin/notifications/history",
] as const;

export function hasAreaAccess(
  menu: readonly MenuItem[] | null | undefined,
  areaCode: string,
): boolean {
  const normalizedAreaCode = normalizeCode(areaCode);
  return (menu ?? []).some((item) =>
    containsAreaCode(item, normalizedAreaCode),
  );
}

export function projectAreaMenu(
  menu: readonly MenuItem[] | null | undefined,
  areaCode: string,
): MenuItem[] {
  const normalizedAreaCode = normalizeCode(areaCode);
  return (menu ?? []).flatMap((item) =>
    projectMenuItem(item, normalizedAreaCode),
  );
}

export function countAreaModules(
  menu: readonly MenuItem[] | null | undefined,
  areaCode: string,
): number {
  return projectAreaMenu(menu, areaCode).length;
}

export function countAreaViews(
  menu: readonly MenuItem[] | null | undefined,
  areaCode: string,
): number {
  const normalizedAreaCode = normalizeCode(areaCode);
  const routes = new Set<string>();
  const visit = (items: readonly MenuItem[]): void => {
    for (const item of items) {
      if (item.permission_type === PermissionType.Action) continue;
      const children = (item.children ?? []).filter(
        (child) => child.permission_type !== PermissionType.Action,
      );
      const route = normalizeRoute(item.route);
      if (
        children.length === 0 &&
        belongsToAreaItem(item, normalizedAreaCode) &&
        isNavigableRoute(route) &&
        route !== `/admin/${normalizedAreaCode.toLowerCase()}`
      ) {
        routes.add(route);
      }
      visit(children);
    }
  };
  visit(projectAreaMenu(menu, normalizedAreaCode));
  if (normalizedAreaCode === STAFF_AREA_CODE) {
    collectStaffNotificationHistoryRoutes(menu ?? []).forEach(route => routes.add(route));
  }
  return routes.size;
}

function collectStaffNotificationHistoryRoutes(menu: readonly MenuItem[]): string[] {
  const collectLeaves = (items: readonly MenuItem[]): MenuItem[] => items.flatMap(item => {
    if (item.permission_type === PermissionType.Action) return [];
    const children = (item.children ?? []).filter(child => child.permission_type !== PermissionType.Action);
    return children.length ? collectLeaves(children) : isNavigableRoute(normalizeRoute(item.route)) ? [item] : [];
  });
  return menu.flatMap(item => {
    if (item.permission_type === PermissionType.Action) return [];
    const code = normalizeCode(item.code);
    if (code === "NOTIFICATIONS" || code === "STAFF-NOTIFICATIONS") {
      const leaves = collectLeaves(item.children ?? []);
      const hasStaffViews = leaves.some(leaf => normalizeCode(leaf.code).startsWith("STAFF-NOTIFICATIONS-"));
      return hasStaffViews
        ? leaves.filter(leaf => normalizeCode(leaf.code) === "NOTIFICATIONS-HISTORY").map(leaf => normalizeRoute(leaf.route))
        : [];
    }
    return collectStaffNotificationHistoryRoutes(item.children ?? []);
  });
}

export function resolveAreaRoute(
  menu: readonly MenuItem[] | null | undefined,
  areaCode: string,
): string | null {
  const normalizedAreaCode = normalizeCode(areaCode);
  const areaMenu = projectAreaMenu(menu, normalizedAreaCode);
  return (
    collectAreaNavigableRoutes(areaMenu, normalizedAreaCode)[0] ??
    collectAreaNavigableRoutes(areaMenu, normalizedAreaCode, true)[0] ??
    null
  );
}

export function resolveKlabTimeRoute(
  menu: readonly MenuItem[] | null | undefined,
): string | null {
  const routes = collectNavigableRoutes(
    menu ?? [],
    isKlabTimeMenuPermission,
    isExcludedFromKlabTime,
  );
  return chooseRoute(routes.filter(route => route.startsWith('/admin/')), "/admin/dashboard");
}

export function projectKlabTimeMenu(
  menu: readonly MenuItem[] | null | undefined,
): MenuItem[] {
  if (!resolveKlabTimeRoute(menu)) return [];

  return (menu ?? []).flatMap((item) =>
    projectMatchingMenuItem(
      item,
      isKlabTimeMenuPermission,
      isExcludedFromKlabTime,
    ),
  );
}

export function projectGlobalMenu(
  menu: readonly MenuItem[] | null | undefined,
): MenuItem[] {
  return (menu ?? []).flatMap((item) =>
    projectFilteredMenuItem(item, isGlobalPermission),
  );
}

export function resolvePermissionRoute(
  menu: readonly MenuItem[] | null | undefined,
  codes: readonly string[],
  routes: readonly string[],
): string | null {
  const normalizedCodes = codes.map(normalizeCode);
  const normalizedRoutes = routes.map(normalizeRoute);
  return chooseRoute(
    collectNavigableRoutes(
      menu ?? [],
      (item) =>
        normalizedCodes.some((code) => belongsToArea(item.code, code)) ||
        normalizedRoutes.some((route) => routeMatches(item.route, route)),
    ),
    normalizedRoutes[0] ?? "",
  );
}

function containsAreaCode(item: MenuItem, areaCode: string): boolean {
  return (
    belongsToAreaItem(item, areaCode) ||
    (item.children ?? []).some((child) => containsAreaCode(child, areaCode))
  );
}

function projectMenuItem(item: MenuItem, areaCode: string): MenuItem[] {
  const code = normalizeCode(item.code);

  if (code === areaCode) {
    return item.children?.length
      ? item.children.map(cloneMenuItem)
      : [{ ...item }];
  }

  if (belongsToAreaItem(item, areaCode)) {
    return [cloneMenuItem(item)];
  }

  const children = (item.children ?? []).flatMap((child) =>
    projectMenuItem(child, areaCode),
  );

  return children.length > 0 ? [{ ...item, children }] : [];
}

function belongsToArea(code: string, areaCode: string): boolean {
  const normalizedCode = normalizeCode(code);
  return (
    normalizedCode === areaCode || normalizedCode.startsWith(`${areaCode}-`)
  );
}

function belongsToAreaItem(item: MenuItem, areaCode: string): boolean {
  return (
    belongsToArea(item.code, areaCode) ||
    routeMatches(item.route, `/admin/${areaCode.toLowerCase()}`)
  );
}

function isKlabTimeMenuPermission(item: MenuItem): boolean {
  return isKlabTimePermission(item) || isKlabTimeAuxiliaryPermission(item);
}

function isExcludedFromKlabTime(item: MenuItem): boolean {
  return item.permission_type === PermissionType.Action ||
    belongsToAreaItem(item, STAFF_AREA_CODE) ||
    normalizeCode(item.code).startsWith('APP-') || routeMatches(item.route, '/app');
}

function isKlabTimePermission(item: MenuItem): boolean {
  return (
    KLAB_TIME_CORE_CODES.some((code) => belongsToArea(item.code, code)) ||
    KLAB_TIME_CORE_ROUTE_PREFIXES.some((route) =>
      routeMatches(item.route, route),
    )
  );
}

function isKlabTimeAuxiliaryPermission(item: MenuItem): boolean {
  return (
    KLAB_TIME_AUXILIARY_CODES.some((code) => belongsToArea(item.code, code)) ||
    KLAB_TIME_AUXILIARY_ROUTE_PREFIXES.some((route) =>
      routeMatches(item.route, route),
    )
  );
}

function isGlobalPermission(item: MenuItem): boolean {
  const code = normalizeCode(item.code);
  const isLeaf = (item.children ?? []).length === 0;
  return (
    GLOBAL_CODES.some((globalCode) => code === globalCode) ||
    (isLeaf &&
      GLOBAL_ROUTE_PREFIXES.some((route) => routeMatches(item.route, route)))
  );
}

function projectFilteredMenuItem(
  item: MenuItem,
  matches: (item: MenuItem) => boolean,
): MenuItem[] {
  const children = (item.children ?? []).flatMap((child) =>
    projectFilteredMenuItem(child, matches),
  );

  return matches(item) || children.length > 0 ? [{ ...item, children }] : [];
}

function projectMatchingMenuItem(
  item: MenuItem,
  matches: (item: MenuItem) => boolean,
  excludesBranch: (item: MenuItem) => boolean,
): MenuItem[] {
  if (excludesBranch(item)) return [];
  if (matches(item)) return [cloneMenuItem(item)];

  const children = (item.children ?? []).flatMap((child) =>
    projectMatchingMenuItem(child, matches, excludesBranch),
  );
  return children.length > 0 ? [{ ...item, children }] : [];
}

function collectNavigableRoutes(
  menu: readonly MenuItem[],
  matches: (item: MenuItem) => boolean,
  excludesBranch: (item: MenuItem) => boolean = () => false,
  inheritedMatch = false,
): string[] {
  return [...menu].sort(compareMenuOrder).flatMap((item) => {
    if (excludesBranch(item)) return [];

    const itemMatches = inheritedMatch || matches(item);
    const route = normalizeRoute(item.route);
    const currentRoute = itemMatches && isNavigableRoute(route) ? [route] : [];
    const childRoutes = collectNavigableRoutes(
      item.children ?? [],
      matches,
      excludesBranch,
      itemMatches,
    );
    return [...currentRoute, ...childRoutes];
  });
}

function compareMenuOrder(a: MenuItem, b: MenuItem): number {
  return a.order - b.order;
}

function collectAreaNavigableRoutes(
  menu: readonly MenuItem[],
  areaCode: string,
  allowCodeRoutes = false,
): string[] {
  const normalizedAreaRoutePrefix = `/admin/${areaCode.toLowerCase()}`;
  return [...menu].sort(compareMenuOrder).flatMap((item) => {
    const route = normalizeRoute(item.route);
    const currentRoute =
      isNavigableRoute(route) &&
      route !== normalizedAreaRoutePrefix &&
      (routeMatches(route, normalizedAreaRoutePrefix) ||
        (allowCodeRoutes && belongsToArea(item.code, areaCode)))
        ? [route]
        : [];
    const childRoutes = collectAreaNavigableRoutes(
      item.children ?? [],
      areaCode,
      allowCodeRoutes,
    );
    return [...currentRoute, ...childRoutes];
  });
}

function chooseRoute(
  routes: readonly string[],
  preferredRoute: string,
): string | null {
  const uniqueRoutes = [...new Set(routes)];
  const preferred = normalizeRoute(preferredRoute);
  return (
    uniqueRoutes.find((route) => route === preferred) ?? uniqueRoutes[0] ?? null
  );
}

function routeMatches(
  route: string | undefined,
  expectedRoute: string,
): boolean {
  const normalizedRoute = normalizeRoute(route);
  const normalizedExpected = normalizeRoute(expectedRoute);
  if (!normalizedRoute || !normalizedExpected) return false;
  return (
    normalizedRoute === normalizedExpected ||
    normalizedRoute.startsWith(`${normalizedExpected}/`)
  );
}

function isNavigableRoute(route: string): boolean {
  return route.length > 0 && route !== "#";
}

function normalizeRoute(route: string | undefined): string {
  const normalizedRoute = String(route ?? "")
    .trim()
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");
  if (!normalizedRoute) return "";
  return normalizedRoute.startsWith("/")
    ? normalizedRoute
    : `/${normalizedRoute}`;
}

function normalizeCode(code: string): string {
  return String(code ?? "")
    .trim()
    .toUpperCase();
}

function cloneMenuItem(item: MenuItem): MenuItem {
  return {
    ...item,
    children: (item.children ?? []).map(cloneMenuItem),
  };
}
